// Estado do modal de saque (ENG-868). Substitui o saque que vivia dentro do
// <BaseModal> genérico (useLayoutStore().modalType === 'withdraw'): agora é
// casco próprio, com etapa e telas de saída em estado local, no mesmo padrão do
// modal de depósito (useGameDepositModal, ENG-361).
//
// Singleton em nível de módulo: os refs vivem fora da função, então todos os
// consumidores compartilham o mesmo estado.

// Nós do stepper. "Detalhes da solicitação" e "Sucesso" NÃO são etapas: são
// saídas do fluxo, por isso vivem em `screen` e não aqui.
export type WithdrawStep = 1 | 2;

export type WithdrawScreen = 'steps' | 'confirm' | 'success' | 'rules';

const isOpen = ref(false);
const step = ref<WithdrawStep>(1);
const screen = ref<WithdrawScreen>('steps');

// Requisição em voo (POST /new-withdraws e as validações que a antecedem).
// Enquanto for true o modal não fecha — nem pelo X, nem pelo clique fora, nem
// pelo ESC, que passam todos pelo mesmo close().
const isBusy = ref(false);

// Contexto "o jogador está sacando", lido por componentes que NÃO são
// descendentes deste modal e que antes olhavam `modalType === 'withdraw'`.
//
// Consumidores EXTERNOS deste flag:
//   components/auth/verification/Kyc.vue                     → copy do KYC de saque
//   components/user/account/BankAccounts/BankAccountsPicker.vue   → label "contas para saque"
//   components/user/account/BankAccounts/BankAccountsListItem.vue → layout do item no saque
//
// Mexeu aqui, confira os três: nenhum deles quebra com erro, só muda de texto
// ou de layout em silêncio.
export const isWithdrawFlowOpen = computed<boolean>(() => isOpen.value);

export function useWithdrawModal() {
  function reset() {
    step.value = 1;
    screen.value = 'steps';
    isBusy.value = false;
  }

  function open() {
    reset();
    isOpen.value = true;
  }

  function close() {
    // Saque em andamento não é interrompido por clique fora/ESC/X.
    if (isBusy.value) return;

    forceClose();
  }

  // Fecha ignorando o guarda de requisição em voo. Só para quem perdeu o direito
  // de continuar vendo o modal — hoje o logout/401 —, nunca para gesto do
  // jogador: aí o caminho é o close().
  function forceClose() {
    isOpen.value = false;
    // Volta ao estado inicial pra próxima abertura começar limpa na Etapa 1.
    reset();
  }

  function goToStep(target: WithdrawStep) {
    screen.value = 'steps';
    step.value = target;
  }

  function goToConfirm() {
    screen.value = 'confirm';
  }

  function goToSuccess() {
    screen.value = 'success';
  }

  // Regras de saque: o Figma não desenha o botão "?" do topbar, mas ele é
  // material de compliance/atendimento que o fluxo antigo oferecia (SPEC 8.3).
  function toggleRules() {
    screen.value = screen.value === 'rules' ? 'steps' : 'rules';
  }

  return {
    isOpen,
    step,
    screen,
    isBusy,
    isWithdrawFlowOpen,
    open,
    close,
    forceClose,
    goToStep,
    goToConfirm,
    goToSuccess,
    toggleRules,
  };
}
